import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { hasFreePass } from "@/lib/constants";
import { MapView } from "@/components/MapView";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Upload, X, Star, MapPin, Plus, Trash2, ImageIcon, Camera, GripVertical } from "lucide-react";
import { Reorder } from "framer-motion";
import { toast } from "sonner";

export default function CreateListing() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const editing = !!id;
  const isPro = !!user && (user.role === "admin" || hasFreePass(user) || (user.plan?.type === "seller_pro_monthly" && user.plan?.status === "active"));

  const [kind, setKind] = useState("sale");
  const [form, setForm] = useState({ title: "", description: "", address: "", city: "", state: "", zip: "", show_contact: false, contact_email: "", contact_phone: "", business_type: "", website: "" });
  const [tags, setTags] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [customTag, setCustomTag] = useState("");
  const [pos, setPos] = useState(null); // [lat,lng]
  const [photos, setPhotos] = useState([]); // storage paths
  const [profileIdx, setProfileIdx] = useState(0);
  const [dates, setDates] = useState([{ _k: "d0", date: "", start: "07:00", end: "22:00" }]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);

  useEffect(() => {
    api.get("/tags").then(({ data }) => setTagOptions(data.all)).catch(() => {});
  }, []);

  useEffect(() => {
    if (editing) {
      api.get(`/listings/${id}`).then(({ data }) => {
        setForm({ title: data.title, description: data.description, address: data.address, city: data.city, state: data.state || "", zip: data.zip, show_contact: !!data.show_contact, contact_email: data.contact_email || "", contact_phone: data.contact_phone || "", business_type: data.business_type || "", website: data.website || "" });
        setKind(data.kind || "sale");
        setPos([data.lat, data.lng]);
        setPhotos(data.photos || []);
        setProfileIdx(data.profile_photo_index || 0);
        setDates(data.sale_dates?.length ? data.sale_dates.map((d, i) => ({ ...d, _k: `d${i}` })) : [{ _k: "d0", date: "", start: "07:00", end: "22:00" }]);
        setTags(data.tags || []);
      }).catch(() => {});
    }
  }, [editing, id]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const geocode = async () => {
    const q = `${form.address}, ${form.city}, ${form.zip}`;
    if (!form.address || !form.city) { toast.error("Enter address and city first"); return; }
    setGeoLoading(true);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}`);
      const data = await res.json();
      if (data[0]) { setPos([parseFloat(data[0].lat), parseFloat(data[0].lon)]); toast.success("Pin placed — drag to fine-tune"); }
      else toast.error("Address not found — drop the pin on the map manually");
    } catch { toast.error("Geocoding failed — place the pin manually"); }
    finally { setGeoLoading(false); }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (photos.length + files.length > 5) { toast.error("Max 5 photos"); return; }
    setUploading(true);
    try {
      const targetId = id || "temp";
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(`/listings/${targetId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, data.path]);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const removePhoto = (i) => {
    setPhotos((p) => p.filter((_, idx) => idx !== i));
    if (profileIdx >= photos.length - 1) setProfileIdx(0);
  };

  const reorderPhotos = (newOrder) => {
    const coverPath = photos[profileIdx];
    setPhotos(newOrder);
    const idx = newOrder.indexOf(coverPath);
    setProfileIdx(idx < 0 ? 0 : idx);
  };

  const save = async () => {
    if (!form.title || !form.description || !form.address || !form.city || !form.zip) { toast.error(kind === "business" ? "Fill in all business details" : "Fill in all sale details"); return; }
    if (kind === "business" && (!form.business_type || !form.website)) { toast.error("Add the business type and website"); return; }
    if (!pos) { toast.error("Place it on the map"); return; }
    setSaving(true);
    const payload = {
      ...form, lat: pos[0], lng: pos[1], photos, profile_photo_index: profileIdx,
      sale_dates: kind === "business" ? [] : dates.filter((d) => d.date).map(({ _k, ...d }) => d),
      tags: kind === "business" ? ["Business", ...tags.filter((t) => t !== "Business")] : tags,
      kind,
    };
    try {
      if (editing) {
        await api.put(`/listings/${id}`, payload);
        toast.success("Listing updated");
        navigate("/profile");
      } else {
        const { data } = await api.post("/listings", payload);
        if (data.status === "active") { toast.success("Listing is live!"); navigate(`/listing/${data.id}`); }
        else {
          toast.success(kind === "business" && !isPro ? "Business listing saved — upgrade to Pro Seller to go live" : "Listing saved — activate it with a plan");
          navigate("/pricing");
        }
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (user && user.role !== "seller" && user.role !== "admin") {
    return (
      <div className="min-h-screen bg-ys-canvas"><Header />
        <div className="max-w-md mx-auto text-center py-20 px-4">
          <MapPin className="w-12 h-12 text-slate-300 mx-auto" />
          <h1 className="font-display text-2xl font-bold mt-4">Switch to a Seller account</h1>
          <p className="text-slate-500 mt-2">Only sellers can post yard sales. Update your role in your profile.</p>
          <Button onClick={() => navigate("/profile")} className="mt-5 rounded-full bg-ys-sky hover:bg-sky-700">Go to Profile</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ys-canvas">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">{kind === "business" ? (editing ? "Edit business listing" : "List your business") : (editing ? "Edit your sale" : "Post a yard sale")}</h1>
        <p className="text-slate-500 mt-1">Add photos, a description, and drop a pin so shoppers can find you.</p>

        <Card className="p-5 sm:p-6 mt-6 rounded-2xl space-y-5">
          {(!editing || isPro) && (
            <div className="flex gap-2 p-1 rounded-full bg-slate-100 w-full sm:w-fit" data-testid="listing-kind-toggle">
              <button type="button" onClick={() => setKind("sale")} data-testid="kind-sale" className={`flex-1 sm:flex-none px-5 h-9 rounded-full text-sm font-semibold ${kind === "sale" ? "bg-white shadow-sm text-ys-sky" : "text-slate-500"}`}>Yard Sale</button>
              <button type="button" onClick={() => setKind("business")} data-testid="kind-business" className={`flex-1 sm:flex-none px-5 h-9 rounded-full text-sm font-semibold ${kind === "business" ? "bg-white shadow-sm text-ys-sky" : "text-slate-500"}`}>Business</button>
            </div>
          )}
          {kind === "business" && !isPro && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2" data-testid="business-pro-note">Business listings go live with a Pro Seller plan — fill in your details and photos, then activate on the next step.</p>
          )}
          <div>
            <Label>{kind === "business" ? "Business name" : "Sale title"}</Label>
            <Input value={form.title} onChange={set("title")} data-testid="listing-title-input" placeholder={kind === "business" ? "Downtown Antiques" : "Multi-family garage sale"} className="mt-1.5 rounded-xl h-12" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={set("description")} data-testid="listing-desc-input" placeholder="Furniture, tools, kids' toys, vintage records…" className="mt-1.5 rounded-xl min-h-[110px]" />
            <div className="mt-4 rounded-2xl border border-slate-200 p-4 bg-slate-50">
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={!!form.show_contact} onChange={(e) => setForm({ ...form, show_contact: e.target.checked })} data-testid="listing-show-contact" className="w-4 h-4 accent-sky-600" />
                <span className="text-sm font-medium">Allow contact details viewable to users in the app?</span>
              </label>
              {form.show_contact && (
                <div className="grid sm:grid-cols-2 gap-3 mt-3">
                  <Input value={form.contact_email} onChange={set("contact_email")} placeholder="Contact email buyers can see" data-testid="listing-contact-email" className="rounded-xl h-11" />
                  <Input value={form.contact_phone} onChange={set("contact_phone")} placeholder="Contact phone buyers can see" data-testid="listing-contact-phone" className="rounded-xl h-11" />
                </div>
              )}
              <p className="text-xs text-amber-600 mt-2 font-medium">⚠ Shown publicly — anyone viewing your listing can see these. Leave blank to use your account email/phone.</p>
            </div>
          </div>

          <div>
            <Label>What are you selling? (tags help shoppers find you)</Label>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="tag-options">
              {tagOptions.map((t) => {
                const on = tags.includes(t);
                return (
                  <button type="button" key={t} data-testid={`tag-${t}`}
                    onClick={() => setTags(on ? tags.filter((x) => x !== t) : [...tags, t])}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${on ? "bg-ys-sky text-white border-ys-sky" : "bg-white text-slate-600 border-slate-300 hover:border-ys-sky"}`}>{t}</button>
                );
              })}
            </div>
            <div className="flex gap-2 mt-2">
              <Input value={customTag} onChange={(e) => setCustomTag(e.target.value)} placeholder="Other… (e.g. Vinyl Records)" data-testid="custom-tag-input" className="rounded-full h-10" />
              <Button type="button" variant="outline" data-testid="add-custom-tag" className="rounded-full gap-1" onClick={() => { const t = customTag.trim(); if (t && !tags.includes(t)) { setTags([...tags, t]); if (!tagOptions.includes(t)) setTagOptions([...tagOptions, t]); } setCustomTag(""); }}><Plus className="w-4 h-4" />Add</Button>
            </div>
          </div>

          {kind === "business" && (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="business-fields">
              <div>
                <Label>Type of business</Label>
                <Input value={form.business_type} onChange={set("business_type")} data-testid="business-type-input" placeholder="e.g. Antique Shop, Café" className="mt-1.5 rounded-xl h-12" />
              </div>
              <div>
                <Label>Website (web address)</Label>
                <Input value={form.website} onChange={set("website")} data-testid="business-website-input" placeholder="https://yourbusiness.com" className="mt-1.5 rounded-xl h-12" />
              </div>
              <div className="sm:col-span-2">
                <Label>Business phone (shows a tap-to-call button)</Label>
                <Input type="tel" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value, show_contact: form.show_contact || !!e.target.value })} data-testid="business-phone-input" placeholder="(555) 123-4567" className="mt-1.5 rounded-xl h-12" />
              </div>
            </div>
          )}
          {/* Photos */}
          <div>
            <Label>Photos (up to 5) — drag to reorder, tap the star to set the cover</Label>
            <div className="flex flex-wrap gap-3 mt-2">
              <Reorder.Group axis="x" values={photos} onReorder={reorderPhotos} className="flex flex-wrap gap-3 list-none m-0 p-0">
                {photos.map((p, i) => (
                  <Reorder.Item key={p} value={p} className="relative w-24 h-24 rounded-xl overflow-hidden border border-slate-200 cursor-grab active:cursor-grabbing" data-testid={`photo-${i}`}>
                    <img src={fileUrl(p)} alt="" className="w-full h-full object-cover pointer-events-none" draggable={false} />
                    <span className="absolute bottom-1 left-1 p-0.5 rounded bg-black/40 pointer-events-none"><GripVertical className="w-3 h-3 text-white" /></span>
                    <button onPointerDown={(e) => e.stopPropagation()} onClick={() => setProfileIdx(i)} data-testid={`set-cover-${i}`}
                      className={`absolute top-1 left-1 p-1 rounded-full ${profileIdx === i ? "bg-ys-gold" : "bg-black/40"}`}>
                      <Star className={`w-3.5 h-3.5 ${profileIdx === i ? "fill-white text-white" : "text-white"}`} />
                    </button>
                    <button onPointerDown={(e) => e.stopPropagation()} onClick={() => removePhoto(i)} data-testid={`remove-photo-${i}`} className="absolute top-1 right-1 p-1 rounded-full bg-black/40"><X className="w-3.5 h-3.5 text-white" /></button>
                  </Reorder.Item>
                ))}
              </Reorder.Group>
              {photos.length < 5 && (
                <div className="w-24 h-24 rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center gap-1.5 text-slate-400 p-2" data-testid="upload-photo-tile">
                  {uploading ? <div className="ys-spinner" style={{ width: 24, height: 24 }} /> : (
                    <>
                      <label data-testid="camera-photo-label" className="w-full flex items-center justify-center gap-1 text-[11px] font-semibold rounded-full bg-ys-sky text-white py-1 cursor-pointer hover:bg-sky-700">
                        <Camera className="w-3 h-3" />Camera
                        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onUpload} data-testid="camera-photo-input" />
                      </label>
                      <label data-testid="gallery-photo-label" className="w-full flex items-center justify-center gap-1 text-[11px] font-semibold rounded-full border border-slate-300 py-1 cursor-pointer hover:border-ys-sky text-slate-600">
                        <ImageIcon className="w-3 h-3" />Gallery
                        <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} data-testid="upload-photo-input" />
                      </label>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Address + map */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label>Street address</Label>
              <Input value={form.address} onChange={set("address")} data-testid="listing-address-input" placeholder="123 Maple St" className="mt-1.5 rounded-xl h-12" />
            </div>
            <div>
              <Label>City</Label>
              <Input value={form.city} onChange={set("city")} data-testid="listing-city-input" className="mt-1.5 rounded-xl h-12" />
            </div>
            <div>
              <Label>State</Label>
              <Input value={form.state} onChange={set("state")} data-testid="listing-state-input" placeholder="CA" className="mt-1.5 rounded-xl h-12" />
            </div>
            <div>
              <Label>Zip code</Label>
              <Input value={form.zip} onChange={set("zip")} data-testid="listing-zip-input" className="mt-1.5 rounded-xl h-12" />
            </div>
          </div>
          <Button onClick={geocode} disabled={geoLoading} variant="outline" data-testid="geocode-btn" className="rounded-full gap-2"><MapPin className="w-4 h-4" />{geoLoading ? "Locating…" : "Find on map"}</Button>
          <div className="rounded-2xl overflow-hidden border border-slate-200 h-64">
            <MapView listings={[]} center={pos || [39.5, -98.35]} zoom={pos ? 15 : 4} picker pickerPosition={pos} onPickerChange={setPos} className="h-full w-full" />
          </div>
          <p className="text-xs text-slate-400 -mt-2">Tap the map or drag the pin to set the exact driveway/yard spot.</p>

          {/* Dates */}
          {kind !== "business" && (
          <div>
            <Label>Sale dates & hours</Label>
            <div className="space-y-2 mt-2">
              {dates.map((d, i) => (
                <div key={d._k} className="flex flex-wrap items-center gap-2" data-testid={`sale-date-${i}`}>
                  <Input type="date" value={d.date} onChange={(e) => setDates(dates.map((x, idx) => idx === i ? { ...x, date: e.target.value } : x))} className="rounded-xl h-11 flex-1 min-w-[140px]" data-testid={`date-input-${i}`} />
                  <Input type="time" value={d.start} onChange={(e) => setDates(dates.map((x, idx) => idx === i ? { ...x, start: e.target.value } : x))} className="rounded-xl h-11 w-32" />
                  <Input type="time" value={d.end} onChange={(e) => setDates(dates.map((x, idx) => idx === i ? { ...x, end: e.target.value } : x))} className="rounded-xl h-11 w-32" />
                  {dates.length > 1 && <button onClick={() => setDates(dates.filter((_, idx) => idx !== i))} className="p-2 text-red-500"><Trash2 className="w-4 h-4" /></button>}
                </div>
              ))}
            </div>
            <Button variant="ghost" onClick={() => setDates([...dates, { _k: `d${Date.now()}`, date: "", start: "07:00", end: "22:00" }])} data-testid="add-date-btn" className="mt-2 gap-1 text-ys-sky"><Plus className="w-4 h-4" />Add another day</Button>
          </div>
          )}

          <Button onClick={save} disabled={saving} data-testid="save-listing-btn" className="w-full rounded-full h-12 bg-ys-sky hover:bg-sky-700 text-base">
            {saving ? "Saving…" : editing ? "Save changes" : "Save & choose plan"}
          </Button>
        </Card>
      </div>
    </div>
  );
}
